<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import {
  fetchChatHistory,
  sendChatMessage,
  type ChatChannel,
  type ChatMessage,
} from '../game/chat-api';
import { BG } from '../game/art';

const channel = ref<ChatChannel>('global');
const messages = ref<ChatMessage[]>([]);
const draft = ref('');
const error = ref('');
const loading = ref(false);
const sending = ref(false);
const listEl = ref<HTMLElement | null>(null);
let poll: ReturnType<typeof setInterval> | null = null;

async function load(scroll = true) {
  loading.value = true;
  error.value = '';
  try {
    messages.value = await fetchChatHistory(channel.value);
    if (scroll) await scrollToBottom();
  } catch (e) {
    error.value = (e as Error).message;
  } finally {
    loading.value = false;
  }
}

async function refresh() {
  try {
    const next = await fetchChatHistory(channel.value);
    const grew = next.length !== messages.value.length ||
      (next.at(-1)?.id !== messages.value.at(-1)?.id);
    messages.value = next;
    if (grew) await scrollToBottom();
  } catch { /* keep current on transient errors */ }
}

async function scrollToBottom() {
  await nextTick();
  if (listEl.value) listEl.value.scrollTop = listEl.value.scrollHeight;
}

async function send() {
  const text = draft.value.trim();
  if (!text || sending.value) return;
  sending.value = true;
  error.value = '';
  try {
    await sendChatMessage(channel.value, text);
    draft.value = '';
    await refresh();
  } catch (e) {
    error.value = (e as Error).message; // includes "Rate limited" from the core
  } finally {
    sending.value = false;
  }
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

onMounted(() => {
  load();
  poll = setInterval(refresh, 5000);
});
onBeforeUnmount(() => { if (poll) clearInterval(poll); });
</script>

<template>
  <section>
    <!-- banner — matches the Jobs/Rankings page treatment -->
    <div class="corner-frame relative rounded-lg overflow-hidden mb-5 h-36">
      <img :src="BG.office" alt="" class="w-full h-full object-cover" />
      <div class="absolute inset-0" style="background:linear-gradient(180deg,rgba(10,7,20,0.25),rgba(10,7,20,0.94))"></div>
      <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
      <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>
      <div class="absolute bottom-3 left-4">
        <h1 class="font-heading text-2xl text-gold">Word on the Street</h1>
        <span class="font-label uppercase tracking-wider text-sm text-text-dim block">The city's one open line — everybody hears it</span>
      </div>
    </div>

  <div class="rounded border border-border bg-surface flex flex-col h-[28rem]">
    <!-- channel header -->
    <div class="flex items-center gap-1 px-3 py-2 border-b border-border">
      <span class="px-3 py-1 text-xs font-label uppercase tracking-wider rounded text-gold">City</span>
      <span class="flex-1"></span>
      <span class="font-body text-[11px] text-text-dim">Open to all of the city</span>
    </div>

    <!-- message list -->
    <div ref="listEl" class="flex-1 overflow-y-auto px-3 py-2 space-y-1.5">
      <p v-if="loading && messages.length === 0" class="font-body text-text-dim text-sm">Loading…</p>
      <p v-else-if="messages.length === 0" class="font-body text-text-dim text-sm">
        No word yet. Break the silence.
      </p>
      <div v-for="m in messages" :key="m.id" class="font-body text-sm leading-snug">
        <span class="text-text-dim text-[11px] mr-1.5">{{ fmtTime(m.createdAt) }}</span>
        <span :class="m.isYou ? 'text-gold' : 'text-gold-muted'" class="font-label text-xs uppercase tracking-wider mr-1.5">
          {{ m.senderUsername ?? 'Unknown' }}
        </span>
        <span class="text-text">{{ m.content }}</span>
      </div>
    </div>

    <!-- composer -->
    <div class="border-t border-border p-2">
      <p v-if="error" class="mb-1.5 text-xs text-error">{{ error }}</p>
      <form class="flex gap-2" @submit.prevent="send">
        <input
          v-model="draft"
          maxlength="500"
          placeholder="Talk to the city…"
          class="flex-1 px-3 py-2 bg-bg border border-border rounded text-text placeholder:text-text-dim focus:border-gold focus:outline-none text-sm"
        />
        <button
          type="submit"
          :disabled="sending || draft.trim().length === 0"
          class="btn-deco btn-deco--gold btn-deco--sm"
        >{{ sending ? '…' : 'Send' }}</button>
      </form>
    </div>
  </div>
  </section>
</template>
